import * as React from "react";

import { PresentationProduct } from "../../model";

import { useProductListUrlStateController } from "./product-list-url-state-controller-provider";

interface ProductListRestoreScrollConsumerInterface {
  getAdditionalClassNameToRenderForEachProductItem: (
    product: PresentationProduct
  ) => string;
  handleAfterFirstMasonryRender: () => void;
}

interface ProductListRestoreScrollConsumer_HigherOrderControllerInterface {
  handleActionProduct: (
    product: PresentationProduct,
    productIndex: number
  ) => void;
}

const defaultValue = {} as unknown as ProductListRestoreScrollConsumerInterface;
const ProductListRestoreScrollContext = React.createContext(defaultValue);

const defaultHigherOrderControllerValue =
  {} as unknown as ProductListRestoreScrollConsumer_HigherOrderControllerInterface;
const ProductListRestoreScrollHigherOrderControllerContext =
  React.createContext(defaultHigherOrderControllerValue);

const SESSION_STORAGE_ACTIONED_PRODUCT = "bbnt_plp_actioned_product";

export const ProductListRestoreScrollProvider: React.FunctionComponent = (
  props
) => {
  const { handleActionProduct: handleActionProduct_forUrlState } =
    useProductListUrlStateController();

  const getAdditionalClassNameToRenderForEachProductItem = React.useCallback(
    (product: PresentationProduct) => {
      return `plp-product-id-${product.id}`;
    },
    []
  );

  const getRenderedProductElement = (productId: string) => {
    const element = window.document.querySelector(
      `.product-masonry .plp-product-id-${productId}`
    );
    return element;
  };

  const handleAfterFirstMasonryRender = React.useCallback(() => {
    const storedClickedProducts = sessionStorage.getItem(
      SESSION_STORAGE_ACTIONED_PRODUCT
    );
    if (storedClickedProducts) {
      const clickedProductId = JSON.parse(storedClickedProducts)[
        btoa(window.location.href)
      ];
      if (clickedProductId) {
        const productToRestore = getRenderedProductElement(clickedProductId);
        if (productToRestore) {
          productToRestore.scrollIntoView({ block: "center" });
        }
      }
    }

    sessionStorage.removeItem(SESSION_STORAGE_ACTIONED_PRODUCT);
  }, []);

  const handleActionProduct = React.useCallback(
    (product: PresentationProduct, productIndex: number) => {
      handleActionProduct_forUrlState(product, productIndex);

      const storedProductClicked = {
        [btoa(window.location.href)]: product.id,
      };
      sessionStorage.setItem(
        SESSION_STORAGE_ACTIONED_PRODUCT,
        JSON.stringify(storedProductClicked)
      );
    },
    [handleActionProduct_forUrlState]
  );

  const value: ProductListRestoreScrollConsumerInterface = React.useMemo(
    () => ({
      getAdditionalClassNameToRenderForEachProductItem,
      handleAfterFirstMasonryRender,
    }),
    [
      getAdditionalClassNameToRenderForEachProductItem,
      handleAfterFirstMasonryRender,
    ]
  );
  const higherOrderControllerValue: ProductListRestoreScrollConsumer_HigherOrderControllerInterface =
    React.useMemo(
      () => ({
        handleActionProduct,
      }),
      [handleActionProduct]
    );
  return (
    <ProductListRestoreScrollContext.Provider value={value}>
      <ProductListRestoreScrollHigherOrderControllerContext.Provider
        value={higherOrderControllerValue}
      >
        {props.children || []}
      </ProductListRestoreScrollHigherOrderControllerContext.Provider>
    </ProductListRestoreScrollContext.Provider>
  );
};

export const useProductListRestoreScroll = () => {
  const value = React.useContext(ProductListRestoreScrollContext);
  if (value === defaultValue) {
    throw new Error(
      "useProductListRestoreScroll must be used within a ProductListRestoreScrollProvider"
    );
  }
  return value;
};

export const useProductListRestoreScroll_asHigherOrderController = () => {
  const value = React.useContext(
    ProductListRestoreScrollHigherOrderControllerContext
  );
  if (value === defaultHigherOrderControllerValue) {
    throw new Error(
      "useProductListRestoreScroll_asHigherOrderController must be used within a ProductListRestoreScrollProvider"
    );
  }
  return value;
};
